import { useMemo } from 'react'
import { fbt } from 'fbtee'
import { GripVertical, Eye, EyeOff, RotateCcw } from 'lucide-react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { cn } from '../../../lib/utils'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog'
import { Button } from '../../../components/ui/button'
import { SectionHeader } from '../../../components/SectionHeader'
import type { Stage } from '../../../types'
import { TimeFormatSection } from './TimeFormatSection'
import { HiddenMarkersSection } from './HiddenMarkersSection'

function SortableStageRow({
  stage,
  hidden,
  onToggleVisibility,
}: {
  stage: Stage
  hidden: boolean
  onToggleVisibility: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stage.id,
  })
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : undefined,
  }
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2',
        hidden && 'opacity-50',
      )}
    >
      <button
        type="button"
        className="text-muted-foreground touch-none cursor-grab active:cursor-grabbing"
        aria-label={fbt(
          'Drag to reorder ' + fbt.param('stage', stage.name),
          'Accessible label for the drag handle on a stage row in schedule settings',
        ).toString()}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-4" />
      </button>
      <span className={cn('text-sm flex-1 truncate', hidden && 'line-through')}>
        {stage.name}
      </span>
      <button
        type="button"
        onClick={onToggleVisibility}
        className="text-muted-foreground cursor-pointer hover:text-foreground transition-colors p-1 -mr-1"
        aria-label={
          hidden
            ? fbt(
                'Show ' + fbt.param('stage', stage.name),
                'Accessible label for the show-stage button in schedule settings',
              ).toString()
            : fbt(
                'Hide ' + fbt.param('stage', stage.name),
                'Accessible label for the hide-stage button in schedule settings',
              ).toString()
        }
      >
        {hidden ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
      </button>
    </div>
  )
}

export function ScheduleSettingsDialog({
  open,
  onOpenChange,
  stages,
  onReorder,
  onToggleVisibility,
  onReset,
  hasCustomSettings,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  stages: Array<Stage & { hidden: boolean }>
  onReorder: (stageIds: string[]) => void
  onToggleVisibility: (stageId: string) => void
  onReset: () => void
  hasCustomSettings: boolean
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const stageIds = useMemo(() => stages.map(s => s.id), [stages])

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e
    if (!over || active.id === over.id) return
    const oldIndex = stages.findIndex(s => s.id === active.id)
    const newIndex = stages.findIndex(s => s.id === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    const reordered = arrayMove(stageIds, oldIndex, newIndex)
    onReorder(reordered)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[70vh] flex flex-col gap-0 p-0">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle>
            <fbt desc="Title of the schedule settings dialog">Schedule settings</fbt>
          </DialogTitle>
          <DialogDescription className="sr-only">
            {fbt(
              'Choose a time format, reorder stages and toggle their visibility.',
              'Accessible description for the schedule settings dialog with time format and stage order',
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4 space-y-3">
          <TimeFormatSection />

          <SectionHeader
            action={
              hasCustomSettings ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="cursor-pointer text-muted-foreground"
                  onClick={() => {
                    onReset()
                    onOpenChange(false)
                  }}
                >
                  <RotateCcw className="size-3.5 mr-1.5" />
                  <fbt desc="Button to reset custom stage order in schedule settings">Reset</fbt>
                </Button>
              ) : undefined
            }
          >
            <fbt desc="Section heading for stage order in schedule settings dialog">Stage order</fbt>
          </SectionHeader>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={stageIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {stages.map(stage => (
                  <SortableStageRow
                    key={stage.id}
                    stage={stage}
                    hidden={stage.hidden}
                    onToggleVisibility={() => onToggleVisibility(stage.id)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          <p className="text-xs text-muted-foreground">
            <fbt desc="Helper text at the bottom of the schedule settings dialog">
              Drag to reorder, tap the eye to hide.
            </fbt>
          </p>

          <HiddenMarkersSection />
        </div>
      </DialogContent>
    </Dialog>
  )
}
